import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MOBILE_STATUSES, MOBILE_SERVICE_LABELS, adminLabelCls as L } from "@/components/admin/adminShared";

export const BLANK_MOBILE = {
  id: "", title: "", client_name: "", client_email: "", client_phone: "", date: "", time: "", end_time: "",
  location: "", attendees: "", event_type: "", services: [], message: "", internal_notes: "", budget: "", status: "pending",
};

const toForm = (ev) => ({
  ...BLANK_MOBILE, ...ev,
  time: ev.time || "", end_time: ev.end_time || "", attendees: ev.attendees ?? "", budget: ev.budget ?? "",
  client_email: ev.client_email || "", client_phone: ev.client_phone || "", services: ev.services || [],
});

function F({ label, children, className = "" }) {
  return <div className={className}><Label className={L}>{label}</Label>{children}</div>;
}

export default function MobileEventDialog({ open, event, onClose, onSaved }) {
  const [e, setE] = useState(BLANK_MOBILE);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setE(event ? toForm(event) : BLANK_MOBILE); }, [open, event]);
  const set = (k) => (ev) => setE({ ...e, [k]: ev.target.value });
  const toggle = (s) => setE({ ...e, services: e.services.includes(s) ? e.services.filter((x) => x !== s) : [...e.services, s] });

  const onSave = async (ev) => {
    ev.preventDefault();
    if (!e.client_name || !e.date) return toast.error("Client i data són obligatoris.");
    const payload = {
      title: e.title, client_name: e.client_name, client_email: e.client_email, client_phone: e.client_phone,
      date: e.date, time: e.time || null, end_time: e.end_time || null, location: e.location,
      attendees: e.attendees !== "" ? Number(e.attendees) : null, event_type: e.event_type, services: e.services,
      message: e.message, internal_notes: e.internal_notes, budget: e.budget !== "" ? Number(e.budget) : null, status: e.status,
    };
    setSaving(true);
    try {
      if (e.id) await api.put(`/mobile/events/${e.id}`, payload);
      else await api.post("/mobile/events", payload);
      toast.success(e.id ? "Esdeveniment extern actualitzat." : "Esdeveniment extern creat.");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Error guardant.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="mobile-event-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl tracking-tight">{e.id ? "Editar esdeveniment extern" : "Nou esdeveniment extern"}</DialogTitle>
          <DialogDescription className="text-[#5C544F]">Els esdeveniments externs no bloquegen la sala: pot haver-hi diversos esdeveniments el mateix dia.</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSave} className="space-y-4">
          <F label="Títol intern"><Input value={e.title} onChange={set("title")} placeholder="Ex: Casament Marta & Pau" className="mt-1" data-testid="mobile-input-title" /></F>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <F label="Client *"><Input value={e.client_name} onChange={set("client_name")} className="mt-1" data-testid="mobile-input-client-name" /></F>
            <F label="Email"><Input type="email" value={e.client_email} onChange={set("client_email")} className="mt-1" data-testid="mobile-input-client-email" /></F>
            <F label="Telèfon"><Input value={e.client_phone} onChange={set("client_phone")} className="mt-1" data-testid="mobile-input-client-phone" /></F>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <F label="Data *"><Input type="date" value={e.date} onChange={set("date")} className="mt-1" data-testid="mobile-input-date" /></F>
            <F label="Inici"><Input type="time" value={e.time} onChange={set("time")} className="mt-1" data-testid="mobile-input-time" /></F>
            <F label="Fi"><Input type="time" value={e.end_time} onChange={set("end_time")} className="mt-1" data-testid="mobile-input-end-time" /></F>
            <F label="Estat">
              <Select value={e.status} onValueChange={(v) => setE({ ...e, status: v })}>
                <SelectTrigger className="mt-1" data-testid="mobile-input-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(MOBILE_STATUSES).map(([k, s]) => <SelectItem key={k} value={k} data-testid={`mobile-status-option-${k}`}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <F label="Ubicació" className="md:col-span-2"><Input value={e.location} onChange={set("location")} className="mt-1" data-testid="mobile-input-location" /></F>
            <F label="Persones"><Input type="number" min="1" value={e.attendees} onChange={set("attendees")} className="mt-1" data-testid="mobile-input-attendees" /></F>
            <F label="Pressupost (€)"><Input type="number" step="0.01" value={e.budget} onChange={set("budget")} className="mt-1" data-testid="mobile-input-budget" /></F>
          </div>
          <F label="Tipus d'esdeveniment"><Input value={e.event_type} onChange={set("event_type")} className="mt-1" data-testid="mobile-input-event-type" /></F>
          <div>
            <p className={L}>Serveis</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {MOBILE_SERVICE_LABELS.map((s, i) => (
                <button type="button" key={s} onClick={() => toggle(s)} data-testid={`mobile-input-service-${i}`} aria-pressed={e.services.includes(s)}
                  className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${e.services.includes(s) ? "bg-[#3e664c] border-[#3e664c] text-white" : "border-[#eae0df] hover:border-[#3e664c]/50"}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
          <F label="Missatge del client"><Textarea value={e.message} onChange={set("message")} rows={3} className="mt-1" data-testid="mobile-input-message" /></F>
          <F label="Notes internes"><Textarea value={e.internal_notes} onChange={set("internal_notes")} rows={3} className="mt-1 bg-[#fdfcf9]" data-testid="mobile-input-internal-notes" /></F>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="mobile-dialog-cancel">Cancel·lar</Button>
            <Button type="submit" disabled={saving} className="rounded-full bg-[#3e664c] hover:bg-[#2e5949] text-white px-6" data-testid="mobile-dialog-save">
              {e.id ? "Guardar canvis" : "Crear esdeveniment"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
